import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Table } from 'antd';
import DisplayText from '../../../common/text/DisplayText';
import { getHasColumnPermissions, getLinkId } from '../../formHelpers';
import { isNullOrUndefined, getIdMap } from '../../../helpers/helpers';
import { getEquipmentCustomDataMap } from '../../../equipment/state/equipment.actions';
import { generateResponseMap } from '../../ResponderHelpers';
import { currencyFormatter } from '../../../helpers/inputParsers';
import { getCustomTableColumn, LINE_NUMBER_COLUMN } from './formFieldsHelpers';
import Permissions from '../../../auth/Permissions';

const currencyColumnKeys = new Set([
  'hourlyCost',
  'hourlyBillingRate',
  'dailyCost',
  'dailyBillingRate',
  'weeklyCost',
  'weeklyBillingRate',
  'monthlyCost',
  'monthlyBillingRate',
]);

export default function AttributeTablePreview({
  title,
  divisions,
  equipment,
  projectEquipment,
  projectId,
  columns,
  previewProps,
  isDisplay,
  id,
  responses,
  setResponses,
  responding,
  configProps,
  showCondensedView,
}) {
  const {
    requiredColumns,
    attrType,
    type,
    linkField,
    columnPermissions = {},
  } = configProps ?? {};

  const positionNames = useSelector((state) => state.settings.positionNames);
  const users = useSelector((state) => state.users.users);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const selected = useMemo(() => (
    responding && responses[id]
      ? responses[id].values
      : previewProps.values || []
  ), [
    responding,
    responses,
    previewProps,
  ]);

  const dispatch = useDispatch();
  const projects = useSelector((state) => state.projects.projects);

  const divSet = useMemo(() => new Set(divisions), [divisions]);

  const [lastProjectId, setLastProjectId] = useState();

  const linkedProjectId = useMemo(() => (
    getLinkId({ responses, defaultValue: projectId, linkField })
  ), [responses, projectId, linkField]);

  const setAttr = (val) => {
    if (!setResponses) return;
    const oldValues = responses[id] ? responses[id].values : [];
    const newValues = isNullOrUndefined(val) ? [] : val;
    if (newValues !== oldValues) {
      setResponses((oldResponses = {}) => ({
        ...oldResponses,
        [id]: {
          ...(oldResponses[id]),
          values: newValues,
        },
      }));
    }
  };

  useEffect(() => {
    const update = async () => {
      switch (attrType) {
        case 'Project': {
          if (selected?.length && linkedProjectId && !lastProjectId) {
            setLastProjectId(linkedProjectId);
            break;
          }
          if (linkedProjectId === lastProjectId) break;
          if (projects && linkedProjectId) {
            const project = projects.find((r) => r.id === linkedProjectId);
            if (project) {
              if (type === 'equipment') {
                const filteredEquipment = equipment.filter((eq) => (
                  eq.active
                    && eq.divisionIds.some((divisionId) => divSet.has(divisionId))
                    && !!projectEquipment[linkedProjectId]?.[eq.id]
                ));
                const res = await dispatch(
                  getEquipmentCustomDataMap({ projectId: linkedProjectId }),
                );
                const { customDataMap } = res || {};
                const filteredEquipmentWithResponses = filteredEquipment.map((eq) => {
                  const { sections } = customDataMap[eq.id] ?? {};
                  const responseMap = sections ? generateResponseMap(sections) : {};
                  return {
                    ...eq,
                    customData: responseMap,
                  };
                });
                setAttr(filteredEquipmentWithResponses);
              } else {
                setAttr([]);
              }
            }
            setLastProjectId(linkedProjectId);
          }
          break;
        }
        default:
          break;
      }
    };
    update();
  }, [
    selected,
    attrType,
    type,
    linkedProjectId,
    lastProjectId,
    equipment,
    projects,
  ]);

  const dataSource = useMemo(() => (
    isDisplay && !responding
      ? previewProps.values || []
      : selected
  ), [isDisplay, responding, previewProps, selected]);

  const onValueChanged = useCallback((rowId, newData = {}) => {
    if (!setResponses) return;
    const newValues = selected.map((row) => {
      if (row.id !== rowId) return row;
      return { ...row, ...newData };
    });
    setResponses((oldResponses = {}) => ({
      ...oldResponses,
      [id]: {
        ...(oldResponses[id]),
        values: newValues,
      },
    }));
  }, [setResponses, selected, id]);

  const tableColumns = useMemo(() => {
    const filteredColumns = columns.filter((col) => (
      getHasColumnPermissions({
        columnPermissions,
        column: col,
        userPositionId,
        userId: Permissions.id,
      })
    ));
    const cols = filteredColumns.map((col) => {
      if (col.isCustomText) {
        return getCustomTableColumn(col, {
          isDisplay,
          requiredColumns,
          responses,
          onChange: (record, value) => onValueChanged(record.id, { [col.key]: value }),
        });
      }
      return {
        title: <div className={requiredColumns && 'form-required-field'}>{col.name}</div>,
        dataIndex: col.key,
        render: (_, record) => {
          if (col.key.startsWith('field-')) return record.customData?.[col.key]?.value;

          const val = record[col.key];
          return currencyColumnKeys.has(col.key) ? currencyFormatter(val ?? 0) : val;
        },
      };
    });
    cols.unshift(LINE_NUMBER_COLUMN);
    return cols;
  }, [
    isDisplay,
    columns,
    requiredColumns,
    columnPermissions,
    userPositionId,
    onValueChanged,
    responses,
  ]);

  return (
    <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
      <Row style={{ width: '100%', marginBottom: 10 }}>
        <Col>
          {title}
        </Col>
      </Row>
      { !showCondensedView || dataSource?.length ? (
        <Table
          className='form-table-preview'
          style={{ width: '100%', overflow: 'auto' }}
          columns={tableColumns}
          size="small"
          pagination={false}
          dataSource={dataSource}
        />
      ) : (
        <DisplayText title="No Values" style={{ marginBottom: 0 }} />
      )}
    </Row>
  );
}

AttributeTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.array,
  configProps: PropTypes.shape({}),
  divisions: PropTypes.array,
  equipment: PropTypes.array,
  equipmentTypeMap: PropTypes.shape({}),
  id: PropTypes.string,
  isDisplay: PropTypes.bool,
  previewProps: PropTypes.shape({}),
  projectEquipment: PropTypes.shape({}),
  projectId: PropTypes.string,
  responding: PropTypes.bool,
  responses: PropTypes.shape({}),
  setResponses: PropTypes.func,
  showCondensedView: PropTypes.bool,
};

AttributeTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  id: null,
  isDisplay: false,
  setResponses: null,
  responses: {},
  responding: false,
  projectId: null,
  divisions: [],
  configProps: {},
  showCondensedView: false,
  projectEquipment: {},
  equipmentTypeMap: {},
};
